import { FC, useMemo, useState } from 'react'
import { toast } from 'react-toastify'
import { ReactComponent as AnalyzingIcon } from 'src/assets/svg/analyzing-48.svg'
import { ReactComponent as CalendarIcon } from 'src/assets/svg/calendar-line-16.svg'
import { ReactComponent as ExportIcon } from 'src/assets/svg/blue-file-download.svg'
import { ReactComponent as EmptyComparisonIcon } from 'src/assets/svg/empty-comparison-48.svg'
import { ReactComponent as WarningTriangleIcon } from 'src/assets/svg/warning-triangle-48.svg'
import {
  CampaignDetailHeader,
  CampaignDetailView,
} from 'src/pages/ThemeDetail/CampaignDetailHeader'
import { DailyMentions } from 'src/pages/ThemeDetail/CampaignDataComparison/DailyMentions'
import { KeyMetricsComparison } from 'src/pages/ThemeDetail/CampaignDataComparison/KeyMetricsComparison'
import { TopSources } from 'src/pages/ThemeDetail/CampaignDataComparison/TopSources'
import {
  useCreateCampaignComparisonMutation,
  useDeleteCampaignComparisonMutation,
} from 'src/reactQuery/useCampaignComparisonMutations'
import { useGetCampaignComparisonQuery } from 'src/reactQuery/useGetCampaignComparisonQuery'
import { useGetCampaignComparisonsQuery } from 'src/reactQuery/useGetCampaignComparisonsQuery'
import { CampaignComparisonApiService } from 'src/services/campaignComparisonApi'
import { cn } from 'src/utils/cn'
import { CampaignKeywords } from './CampaignKeywords'
import { MultiComparison } from './MultiComparison'
import { toMultiReport } from './MultiComparison/reportModel'
import { CampaignSentimentSplit } from './CampaignSentimentSplit'
import { ComparisonHistory } from './ComparisonHistory'
import styles from './campaignComparison.module.scss'
import { toReportModel } from './reportModel'
import {
  CampaignComparisonPayload,
  ComparisonCampaign,
  NewCampaignComparisonModal,
} from './NewCampaignComparisonModal'

interface CampaignComparisonProps {
  backUrl: string
  // Everything the account has; the modal filters and limits the selection.
  campaigns: ComparisonCampaign[]
  onViewChange: (view: CampaignDetailView) => void
  view: CampaignDetailView
}

// Several campaigns measured over ONE window, which is the mirror of Data
// Comparison on a campaign's own page (one campaign over two windows). The
// screens are deliberately the same shape so the two read as one feature.
//
// A comparison is a saved definition, not a snapshot: the server computes its
// report on every read. So there is no run to poll and no report that goes
// stale — the analyzing screen below covers the seconds a computation takes,
// not hours of background work.
const COPY = {
  compareLongerPeriods: 'Compare Longer Periods',
  // A render can fail — the deck is computed on demand — and a button that
  // silently does nothing is what the tester reported.
  downloadFailed: 'We could not prepare this comparison. Please try again.',

  emptyStateSubtitle:
    'Choose your time period and select 2 or more campaigns to get started.',

  emptyStateTitle: 'Start Comparing Your Data',

  exportComparison: 'Export Comparison',

  // While the deck renders. It is produced on demand and takes seconds, so the
  // button has to say it is working — pressed twice it would queue a second
  // render, and the server refuses those past a limit.
  exportingComparison: 'Preparing PDF…',
  failedStateSubtitle:
    'An unexpected error occurred while generating your comparison report. ' +
    'Please try again. If the issue persists, contact support.',
  failedStateTitle: 'Analysis Failed',
  generateAgain: 'Generate Again',
  generatingSubtitle:
    "We're analyzing your selected data to generate meaningful comparison " +
    'insights. This may take a moment depending on the amount of data being ' +
    'processed.',
  generatingTitle: 'Analyzing Your Data & Generating Comparison Report',
  keyMetricsSubtitle:
    'Track how key performance indicators differ between the selected campaigns.',

  // What the trail names on the list: the campaigns these comparisons are of.
  // The feature's own name is already on the switch beside it.
  listTrail: 'Campaigns',

  // Distinct from a short period: the range may be long and the campaigns
  // valid, and still none of them collected anything in it.
  noDataSubtitle:
    'None of the selected campaigns collected any mentions in this period, ' +
    'so there is nothing to compare. Try a longer period, or campaigns that ' +
    'were collecting during it.',

  noDataTitle: 'Not Enough Data to Compare',

  reportTitle: 'Compare Campaigns',
  // On a report the trail says which of the page's two views you are inside,
  // since the report's own name is the heading directly under it.
  reportTrail: 'Comparisons',
  shortPeriodSubtitle:
    'The selected date ranges are too short to generate reliable comparison ' +
    'insights. For more meaningful trends and recommendations, compare longer ' +
    'periods such as a month or a quarter.',
  shortPeriodTitle: 'Short Comparison Period',
  sourcesSubtitle:
    'How mention share differs across sources between the campaigns.',
  startComparison: 'Start Comparison',
  title: 'Campaigns',
}

// A few days gives the report nothing to trend on, so the client stops before
// spending a request to find that out. The same floor the campaign page uses.
const MIN_COMPARISON_DAYS = 7
const MS_PER_DAY = 24 * 60 * 60 * 1000

const DOWNLOAD_FALLBACK_FILENAME = 'campaign-comparison.pdf'

export const CampaignComparison: FC<CampaignComparisonProps> = ({
  backUrl,
  campaigns,
  onViewChange,
  view,
}) => {
  const [isModalOpen, setIsModalOpen] = useState(false)
  // Which comparison's report is open, if any. Held here rather than in the URL
  // because this whole view is a tab of the campaigns page.
  const [openedId, setOpenedId] = useState<string | null>(null)
  // The report's own export button. The list's rows track this themselves,
  // inside the history panel.
  const [isDownloading, setIsDownloading] = useState(false)
  const [isShortPeriod, setIsShortPeriod] = useState(false)

  const { data: list, isLoading: isListLoading } =
    useGetCampaignComparisonsQuery()
  const {
    data: opened,
    isError: isReportError,
    isLoading: isReportLoading,
  } = useGetCampaignComparisonQuery(openedId ?? undefined)

  const createComparison = useCreateCampaignComparisonMutation()
  const deleteComparison = useDeleteCampaignComparisonMutation()

  const comparisons = list?.data ?? []

  // The rows store campaign ids; the names come from the page's own campaign
  // list, which is already loaded.
  const campaignNames = useMemo(
    () =>
      campaigns.reduce<Record<string, string>>(
        (names, campaign) => ({ ...names, [campaign.id]: campaign.name }),
        {}
      ),
    [campaigns]
  )

  const result = opened?.data.result
  /*
   * Two campaigns get the paired report — a delta on every figure, measured
   * against the first. Three or four get the multi report, where there is no
   * single "other" to measure against and the shape is a table or a chart with
   * one series each. One endpoint feeds both; only the view differs.
   */
  const isMulti = (result?.campaigns.length ?? 0) > 2
  const report = result && !isMulti ? toReportModel(result) : undefined

  // Refused only when NOTHING was collected. One campaign at zero is not a
  // reason to withhold the others — that it was silent in the window is itself
  // worth seeing, and the widgets carry it as an empty column.
  //
  // This used to require every campaign to have collected something, which is
  // what QA hit: the warning refused the report at one moment and the saved
  // row opened it at the next, so the app said two different things about the
  // same comparison.
  const multiHasData =
    !!result &&
    result.sentimentSplit.campaigns.some(
      (campaign) => campaign.negative + campaign.neutral + campaign.positive > 0
    )

  const handleSubmit = ({
    campaignIds,
    from,
    quickCompare,
    to,
  }: CampaignComparisonPayload) => {
    setIsModalOpen(false)

    // Inclusive of both endpoints, so Jul 1 – Jul 2 counts as 2 days.
    const days =
      Math.round(
        (new Date(to).getTime() - new Date(from).getTime()) / MS_PER_DAY
      ) + 1

    if (days < MIN_COMPARISON_DAYS) {
      setIsShortPeriod(true)
      return
    }

    setIsShortPeriod(false)

    createComparison.mutate(
      { campaigns: campaignIds, quickCompare, window: { from, to } },
      // Straight to the report it just saved: there is nothing to wait for, and
      // the list is one Back away.
      { onSuccess: (response) => setOpenedId(response.data.id) }
    )
  }

  // Each screen of this view is somewhere different, and the trail says where:
  // the states are still the campaigns page, the list is the comparisons it
  // holds, and a report is one of them. `compact` is the 16px trail Figma puts
  // above a heading of its own; a state screen has no heading above the card,
  // so there the name is the page title at 24px.
  const header = (campaignName: string, compact = true) => (
    <CampaignDetailHeader
      backUrl={backUrl}
      campaignName={campaignName}
      className={styles.embeddedHeader}
      compact={compact}
      // Back steps out of the report first, then out of this view, so it never
      // skips a level.
      onBack={() => {
        if (openedId) {
          setOpenedId(null)

          return
        }

        onViewChange('general')
      }}
      onViewChange={onViewChange}
      parent="Social Listening"
      showActions={false}
      view={view}
    />
  )

  const modal = (
    <NewCampaignComparisonModal
      campaigns={campaigns}
      isOpen={isModalOpen}
      onClose={() => setIsModalOpen(false)}
      onSubmit={handleSubmit}
    />
  )

  const download = async (comparisonId: string) => {
    setIsDownloading(true)

    try {
      await CampaignComparisonApiService.download(
        comparisonId,
        DOWNLOAD_FALLBACK_FILENAME
      )
    } catch {
      toast.error(COPY.downloadFailed)
    } finally {
      // Whatever happened, the button stops claiming to be working.
      setIsDownloading(false)
    }
  }

  // The three states that own the whole view: nothing saved yet, a window too
  // short to report on, and a request that failed. Same card, same layout, so
  // they are one function rather than three copies of it.
  const stateCard = (
    icon: JSX.Element,
    title: string,
    subtitle: string,
    subtitleClassName: string,
    action?: { label: string; onClick: () => void }
  ) => (
    <div className={styles.card}>
      {header(COPY.title, false)}

      <div className={styles.emptyState}>
        {icon}
        <h3 className={styles.emptyStateTitle}>{title}</h3>
        <p className={subtitleClassName}>{subtitle}</p>

        {action && (
          <button
            className={styles.startButton}
            onClick={action.onClick}
            type="button"
          >
            {action.label}
          </button>
        )}
      </div>

      {modal}
    </div>
  )

  // Saving is instant; computing the report is not, so the wait belongs to
  // whichever of the two is in flight.
  if (createComparison.isPending || (!!openedId && isReportLoading)) {
    return (
      <div className={styles.card}>
        {header(COPY.title, false)}

        <div className={styles.emptyState}>
          <AnalyzingIcon className={styles.generatingIcon} />
          <h3 className={cn(styles.emptyStateTitle, styles.generatingTitle)}>
            {COPY.generatingTitle}
          </h3>
          <p className={styles.generatingSubtitle}>{COPY.generatingSubtitle}</p>
        </div>
      </div>
    )
  }

  // Too short a window is fixed in the modal, so this button reopens it rather
  // than starting anything.
  if (isShortPeriod) {
    return stateCard(
      <WarningTriangleIcon
        className={cn(styles.warningIcon, styles.warningIconShortPeriod)}
      />,
      COPY.shortPeriodTitle,
      COPY.shortPeriodSubtitle,
      styles.shortPeriodSubtitle,
      { label: COPY.compareLongerPeriods, onClick: () => setIsModalOpen(true) }
    )
  }

  // The request failed, or the comparison has lost too many of its campaigns to
  // still be one — the server answers 409 for that. Either way there is no
  // report to show.
  if (createComparison.isError || (!!openedId && isReportError)) {
    return stateCard(
      <WarningTriangleIcon
        className={cn(styles.warningIcon, styles.warningIconFailed)}
      />,
      COPY.failedStateTitle,
      COPY.failedStateSubtitle,
      styles.failedStateSubtitle,
      {
        label: COPY.generateAgain,
        onClick: () => {
          setOpenedId(null)
          createComparison.reset()
          setIsModalOpen(true)
        },
      }
    )
  }

  if (result && isMulti && !multiHasData) {
    return stateCard(
      <WarningTriangleIcon
        className={cn(styles.warningIcon, styles.warningIconShortPeriod)}
      />,
      COPY.noDataTitle,
      COPY.noDataSubtitle,
      styles.shortPeriodSubtitle,
      {
        label: COPY.compareLongerPeriods,
        onClick: () => {
          setOpenedId(null)
          setIsModalOpen(true)
        },
      }
    )
  }

  if (result && isMulti) {
    return (
      <>
        <MultiComparison
          header={header(COPY.reportTrail)}
          isDownloading={isDownloading}
          onDownload={() => openedId && download(openedId)}
          quickCompare={opened?.data.quickCompare}
          report={toMultiReport(result)}
        />

        {modal}
      </>
    )
  }

  // One of the two collected nothing, so no widget can be filled on both
  // sides. A report of zeroes looks like an answer and isn't one — this is
  // what the tester hit — so the view says why instead of drawing it.
  if (report && !report.isComparable) {
    return stateCard(
      <WarningTriangleIcon
        className={cn(styles.warningIcon, styles.warningIconShortPeriod)}
      />,
      COPY.noDataTitle,
      COPY.noDataSubtitle,
      styles.shortPeriodSubtitle,
      {
        label: COPY.compareLongerPeriods,
        onClick: () => {
          setOpenedId(null)
          setIsModalOpen(true)
        },
      }
    )
  }

  // The report. Three of its five sections are the campaign page's own: a
  // campaign takes the place of a period there, so the shapes already match and
  // the two reports cannot drift apart.
  if (report) {
    const { comparison, primary } = report.dailyMentions

    return (
      <>
        {/* On the report the header sits on the page background, not inside a
            card: merged into the first card it read as one block with Key
            Metrics. */}
        {header(COPY.reportTrail)}

        <div className={styles.reportTitleRow}>
          <h2 className={styles.reportTitle}>{COPY.reportTitle}</h2>

          <div className={styles.reportTitleActions}>
            <span className={styles.reportPeriod}>
              <CalendarIcon className={styles.reportPeriodIcon} />
              {/* Only when the window came from a shortcut; a hand-picked range
                  has no name but its own dates. */}
              {opened?.data.quickCompare && (
                <span className={styles.reportPeriodRange}>
                  {opened.data.quickCompare}
                </span>
              )}
              <span className={styles.reportPeriodDates}>
                {report.periodLabel}
              </span>
            </span>

            <button
              className={styles.exportButton}
              disabled={isDownloading}
              onClick={() => openedId && download(openedId)}
              type="button"
            >
              <ExportIcon
                className={cn(
                  styles.exportIcon,
                  isDownloading && styles.exportIconWorking
                )}
              />
              {isDownloading ? COPY.exportingComparison : COPY.exportComparison}
            </button>
          </div>
        </div>

        <div className={cn(styles.card, styles.cardReport)}>
          <KeyMetricsComparison
            comparisonLabel={comparison.label}
            insight={report.insight}
            metrics={report.metrics}
            primaryLabel={primary.label}
            subtitle={COPY.keyMetricsSubtitle}
          />
        </div>

        <CampaignSentimentSplit
          campaigns={report.sentimentCampaigns}
          insight={report.sentimentInsight}
        />

        <DailyMentions data={report.dailyMentions} />

        <CampaignKeywords
          period={report.keywordPeriod}
          tables={report.keywords}
        />

        <TopSources
          comparisonLabel={comparison.label}
          primaryLabel={primary.label}
          sources={report.sources}
          subtitle={COPY.sourcesSubtitle}
        />

        {modal}
      </>
    )
  }

  // The comparisons the account has saved; opening a row computes its report.
  if (isListLoading || comparisons.length > 0) {
    return (
      <>
        {header(COPY.listTrail)}

        <div className={cn(styles.card, styles.cardList)}>
          <ComparisonHistory
            campaignNames={campaignNames}
            comparisons={comparisons}
            isLoading={isListLoading}
            onAdd={() => setIsModalOpen(true)}
            onDelete={(id) => deleteComparison.mutate(id)}
            onDownload={download}
            onOpen={setOpenedId}
          />
        </div>

        {modal}
      </>
    )
  }

  return stateCard(
    <EmptyComparisonIcon className={styles.emptyStateIcon} />,
    COPY.emptyStateTitle,
    COPY.emptyStateSubtitle,
    styles.emptyStateSubtitle,
    { label: COPY.startComparison, onClick: () => setIsModalOpen(true) }
  )
}
